import React, { useState, useEffect } from "react";
import {
  Product,
  ProductImage,
  ProductVariant,
  ProductSpecification,
} from "../../services/productService";
import { Category } from "../../services/categoryService";
import { categoryService } from "../../services/categoryService";
import {
  CreateProductRequest,
  ProductImageInput,
} from "../../services/admin/productService";

interface ProductFormProps {
  product: Product | null;
  onSubmit: (data: CreateProductRequest) => void;
  onCancel: () => void;
  error?: string | null;
  isSubmitting?: boolean;
  onErrorClear?: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({
  product,
  onSubmit,
  onCancel,
  error,
  isSubmitting = false,
  onErrorClear,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState<CreateProductRequest>({
    categoryId: "",
    name: "",
    description: "",
    basePrice: 0,
    originalPrice: null,
    discountPercentage: null,
    rating: null,
    ratingCount: null,
    isActive: true,
    inStock: true,
    images: [],
    variants: [],
    specifications: [],
  });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  // Calculate discount percentage when prices change
  useEffect(() => {
    if (
      formData.originalPrice &&
      formData.originalPrice > 0 &&
      formData.basePrice > 0
    ) {
      const discount =
        ((formData.originalPrice - formData.basePrice) /
          formData.originalPrice) *
        100;
      const calculatedDiscount = Math.round(discount * 100) / 100; // Round to 2 decimal places
      if (calculatedDiscount > 0 && calculatedDiscount <= 100) {
        setFormData((prev) => ({
          ...prev,
          discountPercentage: calculatedDiscount,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          discountPercentage: null,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        discountPercentage: null,
      }));
    }
  }, [formData.basePrice, formData.originalPrice]);

  useEffect(() => {
    if (product) {
      // When editing, preserve existing images with their URLs
      // New images will have imageData (base64), existing ones will have imageUrl
      const existingImages = (product.images || []).map((img, index) => ({
        id: img.id,
        imageUrl: img.imageUrl, // Keep existing URL
        displayOrder: img.displayOrder ?? index,
        altText: img.altText || null,
      }));

      setFormData({
        categoryId: product.category.id,
        name: product.name,
        description: product.description || "",
        basePrice: Number(product.basePrice),
        originalPrice: product.originalPrice
          ? Number(product.originalPrice)
          : null,
        discountPercentage: product.discountPercentage
          ? Number(product.discountPercentage)
          : null,
        rating: product.rating ? Number(product.rating) : null,
        ratingCount: product.ratingCount ? Number(product.ratingCount) : null,
        isActive: product.isActive,
        inStock: product.inStock ?? true,
        images: existingImages, // Preserve existing images
        variants: product.variants || [],
        specifications: product.specifications || [],
      });
    }
  }, [product]);

  const loadCategories = async () => {
    try {
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  };

  const processFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    fileArray.forEach((file) => {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert(`الملف ${file.name} ليس صورة. سيتم تخطيه.`);
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(`الصورة ${file.name} كبيرة جداً (أكثر من 5MB). سيتم تخطيه.`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Keep full data URL format (data:image/...;base64,...) for backend
        setFormData((prev) => {
          const newImage: ProductImageInput = {
            id: "",
            imageData: base64, // Send full data URL to backend
            displayOrder: prev.images?.length || 0,
            altText: file.name,
          };
          return {
            ...prev,
            images: [...(prev.images || []), newImage],
          };
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    processFiles(files);
    // Reset input to allow selecting the same file again
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  const removeImage = (index: number) => {
    setFormData((prev) => {
      const newImages = prev.images?.filter((_, i) => i !== index) || [];
      // Update displayOrder to be sequential
      const updatedImages = newImages.map((img, i) => ({
        ...img,
        displayOrder: i,
      }));
      return {
        ...prev,
        images: updatedImages,
      };
    });
  };

  const addVariant = () => {
    setFormData((prev) => ({
      ...prev,
      variants: [
        ...(prev.variants || []),
        {
          id: "",
          variantType: "",
          variantValue: "",
          priceAdjustment: 0,
          isAvailable: true,
          displayOrder: prev.variants?.length || 0,
        },
      ],
    }));
  };

  const updateVariant = (index: number, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      variants:
        prev.variants?.map((v, i) =>
          i === index ? { ...v, [field]: value } : v
        ) || [],
    }));
  };

  const removeVariant = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants?.filter((_, i) => i !== index) || [],
    }));
  };

  const addSpecification = () => {
    setFormData((prev) => ({
      ...prev,
      specifications: [
        ...(prev.specifications || []),
        {
          id: "",
          specKey: "",
          specValue: "",
          displayOrder: prev.specifications?.length || 0,
        },
      ],
    }));
  };

  const updateSpecification = (index: number, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      specifications:
        prev.specifications?.map((s, i) =>
          i === index ? { ...s, [field]: value } : s
        ) || [],
    }));
  };

  const removeSpecification = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      specifications: prev.specifications?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent double submission
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto relative z-50">
        <h2 className="text-2xl font-bold text-[#0d171b] dark:text-white mb-4">
          {product ? "تعديل المنتج" : "إضافة منتج جديد"}
        </h2>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-lg p-4 mb-4 relative z-[60] shadow-lg">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p className="text-red-600 dark:text-red-400 font-semibold mb-1">
                  خطأ في التحقق من البيانات
                </p>
                <p className="text-red-600 dark:text-red-400 text-sm whitespace-pre-wrap">
                  {error}
                </p>
              </div>
              {onErrorClear && (
                <button
                  onClick={onErrorClear}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex-shrink-0"
                  type="button"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                الفئة *
              </label>
              <select
                value={formData.categoryId}
                onChange={(e) =>
                  setFormData({ ...formData, categoryId: e.target.value })
                }
                className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
                required
              >
                <option value="">اختر الفئة</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                الاسم *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              الوصف
            </label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-24 px-3 py-2"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                السعر الأساسي (د.ج) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.basePrice}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    basePrice: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                السعر الأصلي (د.ج)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.originalPrice || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    originalPrice: e.target.value
                      ? parseFloat(e.target.value)
                      : null,
                  })
                }
                className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                نسبة الخصم (%)
              </label>
              <div className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 h-10 px-3 flex items-center">
                {formData.discountPercentage !== null &&
                formData.discountPercentage !== undefined
                  ? `${formData.discountPercentage.toFixed(2)}%`
                  : "0%"}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                محسوبة تلقائياً من السعر الأصلي والأساسي
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                التقييم (0-5)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={formData.rating || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    rating: e.target.value ? parseFloat(e.target.value) : null,
                  })
                }
                className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
                placeholder="0.0 - 5.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                عدد التقييمات
              </label>
              <input
                type="number"
                step="1"
                min="0"
                value={formData.ratingCount || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    ratingCount: e.target.value
                      ? parseInt(e.target.value)
                      : null,
                  })
                }
                className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                className="ml-2 rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-primary"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">
                نشط
              </span>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.inStock ?? true}
                onChange={(e) =>
                  setFormData({ ...formData, inStock: e.target.checked })
                }
                className="ml-2 rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-primary"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">
                متوفر في المخزون
              </span>
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              الصور{" "}
              {formData.images && formData.images.length > 0 && (
                <span className="text-slate-500 dark:text-slate-400 font-normal">
                  ({formData.images.length} صورة)
                </span>
              )}
            </label>

            {/* Image Upload Area */}
            <div className="mb-4">
              <label
                htmlFor="image-upload"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl cursor-pointer transition-all group ${
                  isDragging
                    ? "border-primary bg-primary/10 dark:bg-primary/20 scale-[1.02]"
                    : "border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 hover:border-primary/50"
                }`}
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg
                    className={`w-12 h-12 mb-4 transition-colors ${
                      isDragging
                        ? "text-primary"
                        : "text-slate-400 dark:text-slate-500 group-hover:text-primary"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                    <span className="font-semibold text-primary">
                      انقر للرفع
                    </span>{" "}
                    أو اسحب الصور هنا
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    PNG, JPG, GIF حتى 5MB
                  </p>
                </div>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>

            {/* Image Preview Grid */}
            {formData.images && formData.images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {formData.images.map((img, index) => (
                  <div
                    key={index}
                    className="relative group bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden aspect-square"
                  >
                    <img
                      src={img.imageUrl || img.imageData || ""}
                      alt={img.altText || `صورة ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                        title="حذف الصورة"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        حذف
                      </button>
                    </div>
                    {/* Image order badge */}
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded-full">
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Variants */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                المتغيرات
              </label>
              <button
                type="button"
                onClick={addVariant}
                className="px-3 py-1 bg-primary text-white rounded text-sm"
              >
                إضافة متغير
              </button>
            </div>
            {formData.variants?.map((variant, index) => (
              <div key={index} className="grid grid-cols-5 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="نوع المتغير (مثل: اللون)"
                  value={variant.variantType}
                  onChange={(e) =>
                    updateVariant(index, "variantType", e.target.value)
                  }
                  className="rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3"
                />
                <input
                  type="text"
                  placeholder="القيمة (مثل: أحمر)"
                  value={variant.variantValue}
                  onChange={(e) =>
                    updateVariant(index, "variantValue", e.target.value)
                  }
                  className="rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="تعديل السعر"
                  value={variant.priceAdjustment}
                  onChange={(e) =>
                    updateVariant(
                      index,
                      "priceAdjustment",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3"
                />
                <button
                  type="button"
                  onClick={() => removeVariant(index)}
                  className="px-3 py-1 bg-red-500 text-white rounded"
                >
                  حذف
                </button>
              </div>
            ))}
          </div>

          {/* Specifications */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                المواصفات
              </label>
              <button
                type="button"
                onClick={addSpecification}
                className="px-3 py-1 bg-primary text-white rounded text-sm"
              >
                إضافة مواصفة
              </button>
            </div>
            {formData.specifications?.map((spec, index) => (
              <div key={index} className="grid grid-cols-3 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="المفتاح (مثل: المادة)"
                  value={spec.specKey}
                  onChange={(e) =>
                    updateSpecification(index, "specKey", e.target.value)
                  }
                  className="rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3"
                />
                <input
                  type="text"
                  placeholder="القيمة (مثل: قطن)"
                  value={spec.specValue}
                  onChange={(e) =>
                    updateSpecification(index, "specValue", e.target.value)
                  }
                  className="rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3"
                />
                <button
                  type="button"
                  onClick={() => removeSpecification(index)}
                  className="px-3 py-1 bg-red-500 text-white rounded"
                >
                  حذف
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-[#0d171b] dark:text-white hover:bg-slate-50 dark:hover:bg-slate-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>جاري الحفظ...</span>
                </>
              ) : (
                "حفظ"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
